import dayjs from "dayjs";
import { GoogleCalButton } from "../../components/Appointments";
import { Fragment, useState } from "react";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { AREA_LABEL, LEAD_AREAS, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { CalendarView } from "../../components/Calendar";
import { Empty, ErrorBox, Field, Loading, Panel, Seg, Select, Sheet, fmtDate, money, useToast } from "../../components/ui";
import { openPerson } from "../../components/PersonDrawer";

/** Google Calendar is connected: booked events go there by themselves. */
const useGoogleOn = () => !!useRefData().data?.setting("google")?.connected;
const syncGoogle = () => db.rpc("google_sync_now").catch(() => undefined);

export default function Events() {
  const { can } = useSession();
  const googleOn = useGoogleOn();
  const [editing, setEditing] = useState<any | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [defaults, setDefaults] = useState(false);
  const events = useQuery("events:all", () => db.select("events", { select: "*,event_people(person_id,people(full_name))", order: "event_date.desc.nullslast" }));
  const [view, setView] = useState<"list" | "calendar">(() => { try { return (localStorage.getItem("rca.events.view") as any) || "list"; } catch { return "list"; } });
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const pickView = (v: "list" | "calendar") => { setView(v); try { localStorage.setItem("rca.events.view", v); } catch { /* fine */ } };
  return (
    <>
      <PageHead help="events" title="Events" sub="Every event you find, book or attend, and who you met there."
        actions={can("events", "edit") && <>
          {can("settings", "edit") && <button className="btn" onClick={() => setDefaults(true)}>Default follow-up email</button>}
          <button className="btn primary" onClick={() => setEditing({ status: "Booked" })}>Add event</button></>} />
      <div className="filters"><Seg value={view} onChange={pickView} options={[{ value: "list", label: "List" }, { value: "calendar", label: "Calendar" }]} /></div>
      <ErrorBox error={events.error} />
      {view === "calendar" ? <Panel><div className="panel-body"><CalendarView month={month} onMonth={setMonth}
        items={(events.data ?? []).filter((e: any) => e.event_date).map((e: any) => ({ id: e.id, at: `${e.event_date}T${e.start_time ?? "00:00"}`, allDay: !e.start_time,
          title: e.name ?? "Event", sub: [e.status, e.location].filter(Boolean).join(" · "), tone: e.status === "Attended" ? "gold" : e.status === "Booked" ? "default" : "muted" }))}
        onOpen={(id) => setEditing((events.data ?? []).find((e: any) => e.id === id))} /></div></Panel> :
      <Panel>
        {events.loading && !events.data ? <Loading /> : !events.data?.length ? <Empty title="No events yet">Add the next networking event you plan to attend.</Empty> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Event</th><th>Date</th><th>Status</th><th>Host</th><th className="num">People met</th><th className="num">Cost</th></tr></thead>
              <tbody>
                {events.data.map((e: any) => (
                  <Fragment key={e.id}>
                    <tr onClick={() => setOpen(open === e.id ? null : e.id)}>
                      <td><strong>{e.name}</strong><div className="small muted">{e.location}</div></td>
                      <td className="nowrap">{fmtDate(e.event_date)}{e.start_time && <div className="small muted">{fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}</div>}</td>
                      <td><span className="pill">{e.status}</span></td>
                      <td>{e.host}</td>
                      <td className="num">{e.event_people.length}</td>
                      <td className="num">{money(e.cost)}</td>
                    </tr>
                    {open === e.id && (
                      <tr><td colSpan={6} style={{ background: "var(--paper)" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {e.event_date && (googleOn ? <GoogleStatus e={e} /> : <GoogleCalButton small {...eventCal(e)} />)}
                          {e.event_people.map((p: any) => <PersonChip key={p.person_id} e={e} p={p} editable={can("events", "edit")} />)}
                          {can("events", "edit") && <button className="btn small ghost" onClick={() => setEditing(e)}>Edit event</button>}
                          {can("events", "edit") && <EventFix e={e} onDone={() => { setOpen(null); invalidate("events"); invalidate("people"); }} />}
                        </div>
                        <EventFollowup e={e} editable={can("events", "edit")} />
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>}
      {editing && <EventSheet ev={editing} onClose={() => setEditing(null)} />}
      {defaults && <FollowupDefaults onClose={() => setDefaults(false)} />}
    </>
  );
}

/** Move everyone met at an event to another list, or delete the event (each person keeps "Met at: …"). */
function EventFix({ e, onDone }: { e: any; onDone: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<"" | "move" | "delete">("");
  const [off, setOff] = useState(true);
  const [err, setErr] = useState("");
  const n = e.event_people.length;
  async function move(to: Area) {
    setErr("");
    try {
      const r = await db.rpc<{ moved: number; off_event: number }>("event_move_people", { p_event: e.id, p_to: to, p_off_event: off });
      toast(`${r.moved} moved to ${AREA_LABEL[to]}${r.off_event ? ` and taken off ${e.name}` : ""}`); setMode(""); onDone();
    }
    catch (x) { setErr((x as Error).message); }
  }
  async function del() {
    setErr("");
    try { const r = await db.rpc<{ ok: boolean; noted: number }>("event_delete", { p_event: e.id }); toast(`Event deleted${r.noted ? `; "Met at: ${e.name}" kept on ${r.noted} people` : ""}`); onDone(); }
    catch (x) { setErr((x as Error).message); }
  }
  return <>
    {n > 0 && mode !== "move" && <button className="btn small ghost" onClick={() => setMode("move")}>Move everyone to…</button>}
    {mode === "move" && <span className="toolbar" style={{ margin: 0 }}>
      <span className="small muted">Move {n} {n === 1 ? "person" : "people"} to</span>
      {LEAD_AREAS.filter((a) => a !== "networking").map((a) => <button key={a} className="btn small" onClick={() => move(a)}>{AREA_LABEL[a]}</button>)}
      <button className="btn small ghost" onClick={() => setMode("")}>Cancel</button>
      <label className="small" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <input type="checkbox" checked={off} onChange={(x) => setOff(x.target.checked)} /> Also take them off this event (added by mistake)
      </label>
    </span>}
    {mode !== "delete" ? <button className="btn small ghost danger" onClick={() => setMode("delete")}>Delete event</button> : <span className="toolbar" style={{ margin: 0 }}>
      <span className="small">Delete “{e.name}”? The people stay{n ? ` and each gets a note “Met at: ${e.name}”` : ""}.</span>
      <button className="btn small danger-fill" onClick={del}>Delete</button>
      <button className="btn small ghost" onClick={() => setMode("")}>Keep</button>
    </span>}
    {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
  </>;
}

/** One person met at an event: open them, or take them off it. */
function PersonChip({ e, p, editable }: { e: any; p: any; editable: boolean }) {
  const toast = useToast();
  async function off() {
    try { await db.rpc("event_remove_person", { p_event: e.id, p_person: p.person_id }); invalidate("events"); invalidate(`person:${p.person_id}`); toast(`${p.people?.full_name} taken off ${e.name}`); }
    catch (x) { toast((x as Error).message); }
  }
  return <span className="btn-pair" style={{ display: "inline-flex" }}>
    <button className="btn small" onClick={() => openPerson(p.person_id)}>{p.people?.full_name}</button>
    {editable && <button className="btn small ghost" title={`Not from ${e.name}: take them off this event`} aria-label={`Take ${p.people?.full_name} off this event`} onClick={off}>×</button>}
  </span>;
}

/** Where this event stands with Google Calendar. */
function GoogleStatus({ e }: { e: any }) {
  if (!e.google_event_id && e.event_date < dayjs().subtract(1, "day").format("YYYY-MM-DD")) return null;
  if (!["Booked", "Attended"].includes(e.status)) return <span className="small muted">Not on Google Calendar until it's Booked</span>;
  if (e.google_error) return <span className="small" style={{ color: "var(--alert)" }}>Google Calendar: {e.google_error}</span>;
  return <span className="pill warm" title="Booked events are copied to your Google Calendar automatically, so Calendly sees the time as busy">{e.google_event_id ? "On Google Calendar ✓" : "Going to Google Calendar…"}</span>;
}

const eventCal = (e: any) => ({
  title: e.name ?? "Event", location: e.location, details: [e.host && `Host: ${e.host}`, e.notes].filter(Boolean).join("\n"),
  allDay: !e.start_time,
  start: e.start_time ? dayjs(`${e.event_date}T${e.start_time.slice(0, 5)}`) : dayjs(e.event_date),
  end: e.start_time ? (e.end_time ? dayjs(`${e.event_date}T${e.end_time.slice(0, 5)}`) : dayjs(`${e.event_date}T${e.start_time.slice(0, 5)}`).add(2, "hour")) : null,
});
const fmtTime = (t: string) => dayjs(`2000-01-01T${t}`).format("h:mm A");

function EventSheet({ ev, onClose }: { ev: any; onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const [f, setF] = useState<any>(ev);
  const [error, setError] = useState<Error | null>(null);
  const googleOn = useGoogleOn();
  const set = (k: string) => (v: string) => setF((x: any) => ({ ...x, [k]: v || null }));
  async function save() {
    try {
      if (f.start_time && f.end_time && f.end_time <= f.start_time) throw new Error("The end time must be after the start time.");
      const body = { name: f.name, industry: f.industry, host: f.host, event_date: f.event_date, start_time: f.start_time || null, end_time: f.end_time || null, location: f.location, status: f.status, cost: f.cost ? Number(f.cost) : 0, notes: f.notes };
      if (ev.id) await db.update("events", { id: eq(ev.id) }, body); else await db.insert("events", body);
      if (googleOn) syncGoogle().then(() => invalidate("events"));
      invalidate("events"); toast(googleOn && ["Booked", "Attended"].includes(f.status) ? "Event saved · on your Google Calendar" : "Event saved"); onClose();
    } catch (e) { setError(e as Error); }
  }
  return (
    <Sheet title={ev.id ? "Edit event" : "Add event"} onClose={onClose} footer={<>{f.event_date && !googleOn && <GoogleCalButton {...eventCal(f)} />}{googleOn && <span className="small muted" style={{ marginRight: "auto" }}>{["Booked", "Attended"].includes(f.status) ? "Goes to your Google Calendar when you save" : "Set the status to Booked to put it on your Google Calendar"}</span>}<button className="btn primary" onClick={save}>Save event</button></>}>
      <ErrorBox error={error} />
      <div className="form-grid">
        <Field label="Event name" wide><input className="input" value={f.name ?? ""} onChange={(e) => set("name")(e.target.value)} /></Field>
        <Field label="Date"><input type="date" className="input" value={f.event_date ?? ""} onChange={(e) => set("event_date")(e.target.value)} /></Field>
        <Field label="Starts"><input type="time" className="input" value={(f.start_time ?? "").slice(0, 5)} onChange={(e) => set("start_time")(e.target.value)} /></Field>
        <Field label="Ends"><input type="time" className="input" value={(f.end_time ?? "").slice(0, 5)} onChange={(e) => set("end_time")(e.target.value)} /></Field>
        <Field label="Status"><Select value={f.status} onChange={set("status")} options={ref.data?.list("EventStatus") ?? []} /></Field>
        <Field label="Location"><input className="input" value={f.location ?? ""} onChange={(e) => set("location")(e.target.value)} /></Field>
        <Field label="Host / association"><input className="input" value={f.host ?? ""} onChange={(e) => set("host")(e.target.value)} /></Field>
        <Field label="Industry"><input className="input" value={f.industry ?? ""} onChange={(e) => set("industry")(e.target.value)} /></Field>
        <Field label="Cost ($)"><input type="number" className="input" value={f.cost ?? ""} onChange={(e) => set("cost")(e.target.value)} /></Field>
        <Field label="Notes" wide><textarea className="textarea" value={f.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} /></Field>
      </div>
    </Sheet>
  );
}

// ── The "great meeting you" email, 24 hours after the event ─────────────────────────
const STATE_LABEL: Record<string, string> = { will_get: "Will get it", sent: "Sent", skipped: "Left out", no_email: "No email", opted_out: "Opted out",
  met_before: "Met before", not_people_met: "Not in People met" };
const PLACEHOLDERS = "{first_name} · {event} · {my_name} · {booking_link}";
const toLocal = (iso?: string | null) => (iso ? dayjs(iso).format("YYYY-MM-DDTHH:mm") : "");

function EventFollowup({ e, editable }: { e: any; editable: boolean }) {
  const toast = useToast();
  const tpl = useRefData().data?.setting("event_followup") ?? {};   // the standard text (Default follow-up email)
  const q = useQuery(`events:followup:${e.id}`, () => db.rpc<any>("event_followup", { p_event: e.id }), { staleMs: 0 });
  const [edit, setEdit] = useState<{ subject: string; body: string; at: string } | null>(null);
  const [preview, setPreview] = useState(false);
  const [err, setErr] = useState("");
  // no follow-up row yet comes back as an empty record: treat it as "not set up"
  const f = q.data?.followup?.event_id ? q.data.followup : null;
  const people: { person_id: string; full_name: string; email: string | null; state: string }[] = q.data?.people ?? [];
  const getting = people.filter((p) => p.state === "will_get").length;
  const refresh = () => { invalidate(`events:followup:${e.id}`); };
  async function save(on = true, body = edit) {
    setErr("");
    try {
      const r = await db.rpc<{ ok: boolean; reason?: string }>("event_followup_save", { p_event: e.id, p_subject: body?.subject ?? f?.subject ?? null,
        p_body: body?.body ?? f?.body ?? null, p_send_at: body?.at ? new Date(body.at).toISOString() : f?.send_at ?? null, p_on: on });
      if (!r.ok) throw new Error(r.reason);
      setEdit(null); refresh(); toast(on ? "Follow-up email saved" : "Follow-up email turned off for this event");
    } catch (x) { setErr((x as Error).message); }
  }
  async function sendNow() {
    setErr("");
    try { const r = await db.rpc<any>("event_followup_send_now", { p_event: e.id }); refresh(); toast(`Sent to ${r.queued ?? 0}`); }
    catch (x) { setErr((x as Error).message); }
  }
  async function skip(p: { person_id: string; state: string }) {
    await db.rpc("event_followup_skip", { p_event: e.id, p_person: p.person_id, p_skip: p.state !== "skipped" }); refresh();
  }
  if (q.loading && !q.data) return null;
  if (!f && !people.length) return null;
  const sent = f?.status === "Sent", off = f?.status === "Off";
  return (
    <div className="evf">
      <div className="row-inline" style={{ margin: 0, justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div><b>Follow-up email</b>{" "}
          <span className="small muted">{!f ? "Not set up for this event (people were added before follow-up emails started). Set it up to send them one" : sent ? `Sent ${dayjs(f.sent_at).format("MMM D, h:mm A")} to ${f.sent_count} ${f.sent_count === 1 ? "person" : "people"} · anyone added this week still gets it`
            : off ? "Turned off for this event" : `Goes out ${dayjs(f.send_at).format("ddd MMM D, h:mm A")} to ${getting} ${getting === 1 ? "person" : "people"}`}</span></div>
        {editable && !sent && <div className="row-inline" style={{ margin: 0, gap: 6 }}>
          {f && !edit && <button className="btn small" onClick={() => setPreview(!preview)}>{preview ? "Hide preview" : "Preview"}</button>}
          {!edit && <button className="btn small" onClick={() => setEdit({ subject: f?.subject ?? tpl.subject ?? "", body: f?.body ?? tpl.body ?? "", at: toLocal(f?.send_at ?? q.data?.default_at) })}>Edit email & time</button>}
          {f && !off && !edit && <button className="btn small ghost" onClick={() => save(false, null)}>Turn off</button>}
          {(off || !f) && !edit && <button className="btn small primary" onClick={() => save(true, null)}>{f ? "Turn on" : "Set it up"}</button>}
          {f && !off && !edit && getting > 0 && <button className="btn small ghost" onClick={sendNow}>Send now</button>}
        </div>}
      </div>
      {err && <div className="small" style={{ color: "var(--alert)" }}>{err}</div>}
      {edit && <div className="evf-edit">
        <Field label="Send on (your time)"><input type="datetime-local" className="input" value={edit.at} onChange={(x) => setEdit({ ...edit, at: x.target.value })} /></Field>
        <Field label="Subject" wide><input className="input" value={edit.subject} onChange={(x) => setEdit({ ...edit, subject: x.target.value })} /></Field>
        <Field label="Message" wide><textarea className="textarea" rows={9} value={edit.body} onChange={(x) => setEdit({ ...edit, body: x.target.value })} /></Field>
        <span className="small muted">Filled in for each person: {PLACEHOLDERS}</span>
        <div className="row-inline" style={{ margin: 0, gap: 6 }}>
          <button className="btn small primary" onClick={() => save(true)}>Save</button>
          <button className="btn small ghost" onClick={() => setEdit(null)}>Cancel</button>
        </div>
      </div>}
      {preview && q.data?.preview && <div className="evf-preview">
        <div className="small muted">To {q.data.preview.to} · <b>{q.data.preview.subject}</b></div>
        <iframe title="Follow-up email preview" sandbox="" srcDoc={q.data.preview.html} />
      </div>}
      {people.length > 0 && <div className="evf-people">{people.map((p) => (
        <label key={p.person_id} className={`chip-check small${p.state === "will_get" ? "" : " muted"}`} title={p.email ?? "No email on file"}>
          {(p.state === "will_get" || p.state === "skipped") && editable && !sent
            ? <input type="checkbox" checked={p.state === "will_get"} onChange={() => skip(p)} /> : null}
          {p.full_name} <span className="muted">· {STATE_LABEL[p.state] ?? p.state}</span>
        </label>))}</div>}
    </div>
  );
}

/** The standard follow-up email every new event starts with. */
function FollowupDefaults({ onClose }: { onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const s = ref.data?.setting("event_followup") ?? {};
  const [v, setV] = useState({ on: s.on ?? true, hours_after: s.hours_after ?? 24, subject: s.subject ?? "", body: s.body ?? "" });
  const [err, setErr] = useState<Error | null>(null);
  async function save() {
    try { await db.update("settings", { key: eq("event_followup") }, { value: { ...v, hours_after: Number(v.hours_after) || 24 } }); invalidate("ref:"); toast("Default follow-up email saved"); onClose(); }
    catch (x) { setErr(x as Error); }
  }
  return (
    <Sheet title="Default follow-up email" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={save}>Save</button></>}>
      <ErrorBox error={err} />
      <p className="small muted" style={{ marginTop: 0 }}>When you add someone you met to an event, that event gets this email, sent automatically after the event to everyone met there who has an email. You can change the text and time for each event on the Events page until it goes out.</p>
      <label className="chip-check"><input type="checkbox" checked={!!v.on} onChange={(x) => setV({ ...v, on: x.target.checked })} /> Send a follow-up email after each event</label>
      <div className="form-grid" style={{ marginTop: 10 }}>
        <Field label="Hours after the event ends"><input type="number" min={1} max={168} className="input" value={v.hours_after} onChange={(x) => setV({ ...v, hours_after: x.target.value as any })} /></Field>
        <Field label="Subject" wide><input className="input" value={v.subject} onChange={(x) => setV({ ...v, subject: x.target.value })} /></Field>
        <Field label="Message" wide><textarea className="textarea" rows={11} value={v.body} onChange={(x) => setV({ ...v, body: x.target.value })} /></Field>
      </div>
      <span className="small muted">Filled in for each person: {PLACEHOLDERS}. No end time on the event? It counts from 2 hours after the start, or 6 PM.</span>
    </Sheet>
  );
}
